import type { DeclarationDApp, SettingsDoor } from '@taki/ui';
import { cleDuJeu, definitionsDeJeux, masquesDuJeu } from '@taki/data-model';
import { lireOptionsChamp } from '../studio/client/options.js';

/**
 * CE QUE LE HUB PARAMETRES LIT D'UNE DECLARATION, pour les reglages d'une
 * porte : la liste des portes du rail, et les listes de choix de « Étiquettes
 * et listes ». Module PUR (ni React, ni `import.meta.env`) : le hub et le
 * panneau le lisent, un test Node l'importe tel quel.
 */

/** Prefixe des ids de menu des apps : evite toute collision avec "dock". */
export const APP_MENU_PREFIX = 'app:';

export type PorteDeReglages = SettingsDoor & { menuId: string; label: string };

/**
 * LES PORTES D'UNE APP DANS LE RAIL DES PARAMETRES, TOUTES. Une porte hors
 * navigation (`navigation: false`, la porte Tâches de Timesheet) y a sa ligne
 * comme les autres : on n'y entre que par un lien, mais ses listes et son
 * affichage se reglent ici, et nulle part ailleurs (retour du fondateur du
 * 14/10/2026). Sans elle, `?t=app:<id>/porte:<ecran>/<porte>` retombait sur Dock.
 */
export function portesDeReglages(appId: string, declaration: DeclarationDApp): PorteDeReglages[] {
  return declaration.ecrans.ecrans.flatMap(ecran => ecran.portes.map(porte => ({
    screenId: ecran.id, doorId: porte.id, label: porte.label,
    menuId: `${APP_MENU_PREFIX}${appId}/porte:${encodeURIComponent(ecran.id)}/${encodeURIComponent(porte.id)}`,
  })));
}

export type CatalogueDePorte = ReturnType<typeof lireOptionsChamp> & { jeu: string; id: string };

/**
 * LES LISTES DE CHOIX D'UNE PORTE (ou de toute l'app, sans porte) : TOUTES
 * CELLES DE SON JEU, et rien que ca. Meme resolution et memes cibles que le
 * Studio, choix natifs compris.
 *
 * LES LISTES APPARTIENNENT AU JEU, PAS A LA PORTE NI A L'APP (regle du
 * fondateur du 14/10/2026, `docs/socle/jeu-listes.md`). Toute porte qui montre
 * un jeu, dans toute app, cachee ou non, regle donc les MEMES listes : chaque
 * champ a choix du jeu (le Service qui titre une tache chez TAC compris), puis
 * chaque liste du socle que les portes du jeu citent (Étape, Priorité), hors
 * des champs que le jeu masque (`jeux[].masques`). Rien
 * n'est choisi a la main, rien n'est filtre selon ce que la porte cite : la
 * declaration, recalee sur les jeux de l'org (`declarationCourante`), suffit.
 */
export function cataloguesDePorte(declaration: DeclarationDApp, selection: SettingsDoor | null): CatalogueDePorte[] {
  const ecranChoisi = selection ? declaration.ecrans.ecrans.find(ecran => ecran.id === selection.screenId) : undefined;
  const porteChoisie = ecranChoisi?.portes.find(porte => porte.id === selection?.doorId);
  if (selection && !porteChoisie?.jeu) return [];
  const definitions = definitionsDeJeux(declaration);
  const portes = declaration.ecrans.ecrans.flatMap(ecran => ecran.portes);
  const catalogues: CatalogueDePorte[] = [];
  for (const jeu of declaration.champs.jeux) {
    const cle = cleDuJeu(jeu);
    if (porteChoisie?.jeu && cleDuJeu(porteChoisie.jeu) !== cle) continue;
    /* LE NOM DU JEU, le meme d'une porte et d'une app a l'autre : le sien, ou
       celui que sa premiere porte lui donne. */
    const premiere = portes.find(porte => porte.jeu !== undefined && cleDuJeu(porte.jeu) === cle);
    const nomFiche = jeu.nom || premiere?.jeu?.nom || premiere?.label || jeu.type || jeu.family;
    /* UN CHAMP MASQUE (`jeux[].masques`) ne se montre sur aucune fiche : sa liste ne se regle pas ici non plus. */
    const masques = masquesDuJeu(jeu);
    const aChoix = jeu.champs.filter(champ => (champ.type === 'select' || champ.type === 'multiselect') && !masques.has(champ.key)).map(champ => champ.key);
    const natives = Object.keys(definitions.get(cle)?.natives ?? {});
    for (const field of [...aChoix, ...natives]) {
      const cible = { family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }), field };
      catalogues.push({ ...lireOptionsChamp(declaration, cible), jeu: nomFiche, id: `${jeu.family}:${jeu.type ?? ''}:${field}` });
    }
  }
  return catalogues;
}
